import { getAppVersion } from '../utils/version';
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import { Menu, LogOut, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { MainNavItem } from '../hooks/navigation/useNavigationData';
import { NavigationMenuItem, settingsMenuItems, profileMenuItems, getRouteTitle, isMenuItemActive, isFeatureVisible } from './navigationConfig';
import { useFeatures } from '../hooks/useFeatures';
import { useTenantTerm } from '../hooks/useTenantTerm';
import { SMALL_SCREEN_QUERY } from '../hooks/useIsSmallScreen';
import './MobileMenu.css';

interface MobileMenuProps {
    mainNavItems: MainNavItem[];
    isAdmin: boolean;
    isSuperadmin: boolean;
    userName: string;
    organizationName: string;
    customerLogo: string | undefined;
    activeTodosCount: number;
    logOut: () => Promise<void>;
    /** Set for superadmins: opens the building assistant. */
    onOpenAssistant?: () => void;
}

// Top bar (hamburger, section title, logo) shown below the mobile breakpoint,
// plus the slide-in drawer it opens. Renders from the same useNavigationData()
// output and navigationConfig lists as the desktop sidebar so the two can't drift.
const MobileMenu = ({
    mainNavItems,
    isAdmin,
    isSuperadmin,
    userName,
    organizationName,
    customerLogo,
    activeTodosCount,
    logOut,
    onOpenAssistant,
}: MobileMenuProps) => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const features = useFeatures();
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const [profileExpanded, setProfileExpanded] = useState(false);
    // Main sections with sub-pages (Hyresadmin, Parkering), keyed by path.
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

    const isOnSettingsRoute = location.pathname.startsWith('/settings') || location.pathname.startsWith('/superadmin');
    const isOnProfileRoute = profileMenuItems.some((item) => isMenuItemActive(item, location.pathname));

    // Close on any route change, including browser back/forward while open.
    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    // Close if the viewport grows past the breakpoint, where the top bar is hidden.
    useEffect(() => {
        const mediaQuery = window.matchMedia(SMALL_SCREEN_QUERY);
        const handleChange = (event: MediaQueryListEvent) => {
            if (!event.matches) setIsOpen(false);
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const open = () => {
        // Expand the section the user is currently in so its active item is visible.
        setSettingsExpanded(isOnSettingsRoute);
        setProfileExpanded(isOnProfileRoute);
        setExpandedSections(new Set(mainNavItems.filter((item) => item.children && item.isActive).map((item) => item.path)));
        setIsOpen(true);
    };

    const close = () => setIsOpen(false);

    const tenantsLabel = useTenantTerm().plural;
    const pageTitle = getRouteTitle(location.pathname, tenantsLabel);
    const profileImage = customerLogo ?? '/Images/Menu/meny-user.png';

    const renderSubItems = (items: NavigationMenuItem[]) =>
        items
            .filter((item) => (!item.superadminOnly || isSuperadmin) && isFeatureVisible(item.feature, features))
            .map((item) => (
                <React.Fragment key={item.path}>
                    {item.dividerBefore && <div className="mobile-drawer-divider" />}
                    <Link
                        to={item.path}
                        className={`mobile-drawer-subitem ${isMenuItemActive(item, location.pathname) ? 'active' : ''}`}
                        onClick={close}
                    >
                        <item.icon size={18} />
                        <span>{item.label}</span>
                        {item.path === '/my-todos' && activeTodosCount > 0 && (
                            <span className="mobile-drawer-badge">{activeTodosCount}</span>
                        )}
                    </Link>
                </React.Fragment>
            ));

    return (
        <>
            <header className="mobile-topbar">
                <button
                    type="button"
                    className="mobile-topbar-icon-button"
                    onClick={open}
                    aria-label="Öppna meny"
                    aria-expanded={isOpen}
                >
                    <Menu size={24} />
                </button>
                <span className="mobile-topbar-title">{pageTitle}</span>
                {onOpenAssistant && (
                    <button
                        type="button"
                        className="mobile-topbar-icon-button"
                        onClick={onOpenAssistant}
                        aria-label="Öppna byggnadsassistenten"
                    >
                        <Sparkles size={22} />
                    </button>
                )}
                <Link
                    to="/"
                    className="mobile-topbar-icon-button"
                    onClick={close}
                    aria-label="Till startsidan"
                >
                    <img src="/Images/Menu/mf-logo.png" alt="" className="mobile-topbar-logo" />
                </Link>
            </header>

            <Offcanvas
                show={isOpen}
                onHide={close}
                placement="start"
                className="mobile-drawer"
                aria-label="Huvudmeny"
            >
                <Offcanvas.Header closeButton closeVariant="white" closeLabel="Stäng meny">
                    <div className="mobile-drawer-user">
                        <img src={profileImage} alt="" className="mobile-drawer-user-logo" />
                        <div className="mobile-drawer-user-text">
                            <div className="mobile-drawer-user-name">{userName}</div>
                            <div className="mobile-drawer-user-org">{organizationName}</div>
                        </div>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <nav className="mobile-drawer-main-items">
                        {mainNavItems.map((item) => item.children ? (
                            <div key={item.path} className="mobile-drawer-section">
                                <button
                                    type="button"
                                    className={`mobile-drawer-item ${item.isActive ? 'active' : ''}`}
                                    onClick={() => setExpandedSections((current) => {
                                        const next = new Set(current);
                                        if (!next.delete(item.path)) next.add(item.path);
                                        return next;
                                    })}
                                    aria-expanded={expandedSections.has(item.path)}
                                >
                                    <img src={item.icon} alt="" className="mobile-drawer-item-icon" />
                                    <span>{item.label}</span>
                                    {expandedSections.has(item.path) ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>
                                {expandedSections.has(item.path) && (
                                    <div className="mobile-drawer-subitems">{renderSubItems(item.children)}</div>
                                )}
                            </div>
                        ) : (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`mobile-drawer-item ${item.isActive ? 'active' : ''}`}
                                onClick={close}
                                aria-current={item.isActive ? 'page' : undefined}
                            >
                                <img src={item.icon} alt="" className="mobile-drawer-item-icon" />
                                <span>{item.label}</span>
                            </Link>
                        ))}
                    </nav>

                    <div className="mobile-drawer-divider" />

                    {isAdmin && (
                        <div className="mobile-drawer-section">
                            <button
                                type="button"
                                className={`mobile-drawer-item ${isOnSettingsRoute ? 'active' : ''}`}
                                onClick={() => setSettingsExpanded((expanded) => !expanded)}
                                aria-expanded={settingsExpanded}
                            >
                                <img src="/Images/Menu/meny-settings.png" alt="" className="mobile-drawer-item-icon" />
                                <span>Inställningar</span>
                                {settingsExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>
                            {settingsExpanded && (
                                <div className="mobile-drawer-subitems">{renderSubItems(settingsMenuItems)}</div>
                            )}
                        </div>
                    )}

                    <div className="mobile-drawer-section">
                        <button
                            type="button"
                            className={`mobile-drawer-item ${isOnProfileRoute ? 'active' : ''}`}
                            onClick={() => setProfileExpanded((expanded) => !expanded)}
                            aria-expanded={profileExpanded}
                        >
                            <img src={profileImage} alt="" className="mobile-drawer-item-icon mobile-drawer-item-icon-round" />
                            <span>Mitt konto</span>
                            {activeTodosCount > 0 && !profileExpanded && (
                                <span className="mobile-drawer-badge">{activeTodosCount}</span>
                            )}
                            {profileExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                        {profileExpanded && (
                            <div className="mobile-drawer-subitems">{renderSubItems(profileMenuItems)}</div>
                        )}
                    </div>

                    <div className="mobile-drawer-divider" />

                    <button
                        type="button"
                        className="mobile-drawer-item mobile-drawer-logout"
                        onClick={() => {
                            close();
                            logOut();
                        }}
                    >
                        <LogOut size={32} strokeWidth={1.5} />
                        <span>Logga ut</span>
                    </button>

                    <Link to="/versionshistorik" className="mobile-drawer-version" onClick={close}>
                        v{getAppVersion().version}
                    </Link>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default MobileMenu;
